import { useState } from "react";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import FormField from "./FormField";
import { AUDIT_TYPES, AU_STATES } from "./constants";

const EMPTY = {
  org_name: "", abn: "", contact_name: "", contact_phone: "", contact_email: "",
  website: "", state: "", lead_source: "", owner_id: "",
  likely_audit_type: "Unknown", notes: "", interest_notes: "", interested_groups: [],
};

export default function LeadDialog({ ref_, onClose, onSaved, initial }) {
  const [f, setF] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  async function save(e) {
    e.preventDefault();
    if (!f.org_name || !f.contact_name || !f.contact_email) {
      toast.error("Organisation, contact name and email are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/crm/leads", f);
      toast.success("Lead added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add lead</DialogTitle>
          <DialogDescription>Capture a new lead and place them in the sales pipeline.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
          <FormField label="Organisation *"><Input value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="lead-org" /></FormField>
          <FormField label="ABN"><Input value={f.abn} onChange={(e) => setF({ ...f, abn: e.target.value })} /></FormField>
          <FormField label="Contact name *"><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="lead-contact" /></FormField>
          <FormField label="Contact email *"><Input type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="lead-email" /></FormField>
          <FormField label="Phone"><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></FormField>
          <FormField label="Website"><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></FormField>
          <FormField label="State">
            <Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Lead source">
            <Select value={f.lead_source} onValueChange={(v) => setF({ ...f, lead_source: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{(ref_.sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Assigned to">
            <Select value={f.owner_id} onValueChange={(v) => setF({ ...f, owner_id: v })}>
              <SelectTrigger><SelectValue placeholder="Me" /></SelectTrigger>
              <SelectContent>{(ref_.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Likely audit type">
            <Select value={f.likely_audit_type} onValueChange={(v) => setF({ ...f, likely_audit_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{AUDIT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Notes" full>
            <Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </FormField>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="save-lead-button">
              {saving ? "Saving…" : "Add lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
